import S from 's-js';
import { lift } from 's-array';
import uniqueIndex from 'lib/uniqueIndex';
import { IAjax } from 'lib/Ajax';

import { Comment } from 'data/models/Comment';
import { TaggedCommentUser } from 'data/models/TaggedCommentUser';
import { UploadFileResponseData } from 'data/models/UploadFileResponse';

import { PCCache } from 'data/PCCache';

import { PersonProfilePopupParams, PersonProfilePopup } from 'portcall/personprofilepopup/model';
import { UploadFile, UploadFileParams } from 'portcall/uploadfile/model';
import { UserSelector } from 'portcall/userselector/model';

export interface NewCommentParams {
    url : () => string;
}

export type NewComment = ReturnType<typeof NewComment>;
export function NewComment(
    ajax : IAjax,
    cache : PCCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    uploadFileModel : (params : UploadFileParams<UploadFileResponseData>) => UploadFile,
    { url } : NewCommentParams
) {
    const
        client = ajax.Url<Comment>(url, { useCache: true, include: [c => c.TaggedUsers] }),
        NewComment = S.value(new Comment()),
        // S.data not S.value so we can keep re-triggering focus as needed.
        MessageFocusTriggered = S.data(false),
        RecipientSelector = S(() => NewRecipientSelector(NewComment(), cache)),
        Recipients = lift(() => NewComment().TaggedUsers())
            .map(t => CommentRecipient(NewComment, t, cache, personProfilePopupModel)),
        FileUploader = uploadFileModel({
            onComplete: json => S.freeze(() => {
                NewComment().AttachmentUrl(json.Url);
                MessageFocusTriggered(true);
            })
        });

    return {
        NewComment,
        MessageFocusTriggered,
        RecipientSelector,
        Recipients,
        FileUploader,
        CanSubmit,
        Submit
    };

    function CanSubmit() {
        // Gotta have a URL to Post to and have to have specified some content.
        return !!url() && !!(NewComment().Message()?.trim() || NewComment().AttachmentUrl());
    }

    function Submit() {
        if (CanSubmit()) client.Post(NewComment()).then(() => NewComment(new Comment()));
    }
}

export type NewRecipientSelector = ReturnType<typeof NewRecipientSelector>;
function NewRecipientSelector(
    comment : Comment,
    cache : PCCache
) {
    const
        selectedUserId = S.value(0),
        taggedUsersIdx = uniqueIndex(comment.TaggedUsers, t => t.UserId()),
        taggableUsers = lift(() => Array.from(cache.PCUsers.items()))
            .filter(u => !taggedUsersIdx(u.Id())())
            .filter(u => u.Active()) ?? [],
        Selector = UserSelector({
            Users: taggableUsers,
            UserId: selectedUserId
        }),
        IsVisible = S.value(false),
        // S.data not S.value so we can keep re-triggering focus as needed.
        SelectorFocusTriggered = S.data(false);

    S.on(selectedUserId, () => {
        if (selectedUserId()) {
            S.freeze(() => {
                comment.TaggedUsers.push(new TaggedCommentUser({ CommentId: 0, UserId: selectedUserId()}));
                Selector.data("");
            });
        }
    });

    return {
        Selector,
        IsVisible,
        Focus,
        SelectorFocusTriggered
    };

    function Focus() {
        S.freeze(() => {
            IsVisible(true);
            SelectorFocusTriggered(true);
        });
    }
}

export type CommentRecipient = ReturnType<typeof CommentRecipient>;
function CommentRecipient(
    comment : () => Comment,
    taggedUser : TaggedCommentUser,
    cache : PCCache,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup
) {
    const
        User = cache.PCUsers.update1(null, { Id: taggedUser.UserId() }),
        showProfile = S.value(false),
        ProfilePopup = S(() => showProfile() ? personProfilePopupModel({
            id: taggedUser.UserId(),
            onHidePopup: () => showProfile(false)
        }) : null);

    return {
        User,
        ToggleProfile: () => showProfile(!showProfile()),
        ProfilePopup,
        Delete
    };

    function Delete() {
        comment().TaggedUsers.remove(taggedUser);
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/eventlog/newcomment.ts